import { Component, EventEmitter, Input, OnInit, Optional, Output, TemplateRef, ChangeDetectorRef } from '@angular/core';
import {
  ConfirmationModalWindowInputKeys,
  ConfirmationModalWindowSharedContext,
  ConfirmationModalWindowSharedContextInputKeys,
} from './constants';
import { ComponentSwitcherDirective } from '@anecdotes/design-system/component-switcher';
import { filter } from 'rxjs/operators';
import { ModalWindowService } from '../../services/modal-window/modal-window.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-confirmation-modal-window',
  templateUrl: './confirmation-modal-window.component.html',
  styleUrls: ['./confirmation-modal-window.component.scss'],
  standalone: false,
})
@Input()
export class ConfirmationModalWindowComponent implements OnInit {
  private contextData: ConfirmationModalWindowSharedContext;

  @Input(ConfirmationModalWindowInputKeys.confirmTranslationKey)
  confirmTranslationKey: string;

  @Input(ConfirmationModalWindowInputKeys.dismissTranslationKey)
  dismissTranslationKey: string;

  @Input(ConfirmationModalWindowInputKeys.dismissButtonId)
  dismissButtonId?: string;

  @Input(ConfirmationModalWindowInputKeys.questionTranslationKey)
  questionTranslationKey: string;

  @Input(ConfirmationModalWindowInputKeys.questionTranslationParams)
  questionTranslationParams: any;

  @Input(ConfirmationModalWindowInputKeys.aftermathTranslationKey)
  aftermathTranslationKey: string;

  @Input(ConfirmationModalWindowInputKeys.aftermathTranslationParams)
  aftermathTranslationParams: any;

  @Input(ConfirmationModalWindowInputKeys.aftermathTemplate)
  aftermathTemplate: TemplateRef<any>;

  @Input(ConfirmationModalWindowInputKeys.icon)
  icon: string;

  @Input(ConfirmationModalWindowInputKeys.confirmationHandlerFunction)
  confirmationHandlerFunction: () => Promise<void>;

  @Input(ConfirmationModalWindowInputKeys.dismissHandlerFunction)
  dismissHandlerFunction: () => Promise<void>;

  @Input(ConfirmationModalWindowInputKeys.closeModalAfterDismissing)
  closeModalAfterDismissing = false;

  @Input(ConfirmationModalWindowInputKeys.keepModalOpenedAfterConfirming)
  keepModalOpenedAfterConfirming = false;

  @Input(ConfirmationModalWindowInputKeys.translationKeyAfterConfirming)
  translationKeyAfterConfirming: string;

  @Input(ConfirmationModalWindowInputKeys.iconPathAfterConfirming)
  iconPathAfterConfirming: string;

  @Input(ConfirmationModalWindowInputKeys.displayTimeForModifiedTextAfterConfirming)
  displayTimeForModifiedTextAfterConfirming: number;

  @Input(ConfirmationModalWindowInputKeys.confirmButtonClasses)
  confirmButtonClasses: string;

  @Input(ConfirmationModalWindowInputKeys.successWindowSwitcherId)
  successWindowSwitcherId: string;

  @Input(ConfirmationModalWindowInputKeys.errorWindowSwitcherId)
  errorWindowSwitcherId: string;

  @Input(ConfirmationModalWindowInputKeys.localStorageKey)
  localStorageKey: string;

  @Input(ConfirmationModalWindowInputKeys.dontShowTranslationKey)
  dontShowTranslationKey: string;

  @Input(ConfirmationModalWindowInputKeys.disableConfirmationButton)
  disableConfirmationButton: () => boolean;

  @Input(ConfirmationModalWindowInputKeys.primaryButtonFirst)
  primaryButtonFirst = true;

  @Input(ConfirmationModalWindowInputKeys.confirmIcon)
  confirmIcon: string;

  @Input(ConfirmationModalWindowInputKeys.dismissIcon)
  dismissIcon: string;

  @Output()
  confirmClick = new EventEmitter(true);

  @Output()
  dismissClick = new EventEmitter(true);

  isLoading: boolean;
  isConfirmed = false;
  modifiedConfirmTranslationKey: string;
  localStorageValue: boolean | string = '';

  get isConfirmTranslationKeyShouldBeChanged(): boolean {
    return this.isConfirmed && !!this.translationKeyAfterConfirming;
  }

  get isConfirmationValid(): boolean {
    if (this.disableConfirmationButton) {
      return !this.disableConfirmationButton() && !!this.confirmTranslationKey;
    }
    return true;
  }

  constructor(
    private modalWindowService: ModalWindowService,
    private cd: ChangeDetectorRef,
    @Optional() private switcher: ComponentSwitcherDirective,
  ) {}

  ngOnInit(): void {
    if (this.switcher?.sharedContext$) {
      this.getSwitcherContextData();
    }
    this.modifiedConfirmTranslationKey = this.confirmTranslationKey;
  }

  async confirm(): Promise<void> {
    if (this.contextData) {
      this.isLoading = true;
      try {
        if (this.localStorageKey) {
          localStorage.setItem(this.localStorageKey, String(this.localStorageValue));
        }
        if (this.confirmationHandlerFunction) {
          await this.confirmationHandlerFunction();
        }
        this.isLoading = false;
        this.isConfirmed = true;
        if (this.successWindowSwitcherId) {
          this.switcher.goById(this.successWindowSwitcherId);
        } else if (!this.keepModalOpenedAfterConfirming) {
          this.modalWindowService.close();
        }

        if (this.translationKeyAfterConfirming) {
          this.modifiedConfirmTranslationKey = this.translationKeyAfterConfirming;
          // We add this to display temporary X text for Y seconds after the confirmed modal
          setTimeout(() => {
            this.modifiedConfirmTranslationKey = this.confirmTranslationKey;
            this.isConfirmed = false;
            this.cd.detectChanges();
          }, this.displayTimeForModifiedTextAfterConfirming);
        }
      } catch {
        this.isLoading = false;

        if (this.errorWindowSwitcherId) {
          this.switcher.goById(this.errorWindowSwitcherId);
        } else {
          this.modalWindowService.close();
        }
      }
    } else {
      this.modalWindowService.close();
    }
    this.confirmClick.emit();
  }

  async dismiss(): Promise<void> {
    if (this.switcher) {
      if (this.dismissHandlerFunction) {
        await this.dismissHandlerFunction();
        if (this.closeModalAfterDismissing) {
          this.modalWindowService.close();
        }
        return;
      } else if (this.switcher.previousIndex) {
        if (this.closeModalAfterDismissing) {
          this.modalWindowService.close();
        }
        this.switcher.goBack();
        return;
      }
    }
    this.dismissClick.emit();
    this.modalWindowService.close();
  }

  private async getSwitcherContextData(): Promise<void> {
    this.contextData = await firstValueFrom(this.switcher.sharedContext$
      .pipe(filter(Boolean)));

    this.confirmTranslationKey = this.confirmTranslationKey
      ? this.confirmTranslationKey
      : this.contextData[ConfirmationModalWindowSharedContextInputKeys.confirmTranslationKey];
    this.dismissTranslationKey = this.dismissTranslationKey
      ? this.dismissTranslationKey
      : this.contextData[ConfirmationModalWindowSharedContextInputKeys.dismissTranslationKey];
    this.questionTranslationKey = this.questionTranslationKey
      ? this.questionTranslationKey
      : this.contextData[ConfirmationModalWindowSharedContextInputKeys.questionTranslationKey];
    this.questionTranslationParams = this.questionTranslationParams
      ? this.questionTranslationParams
      : this.contextData[ConfirmationModalWindowSharedContextInputKeys.questionTranslationParams];
    this.aftermathTranslationKey = this.aftermathTranslationKey
      ? this.aftermathTranslationKey
      : this.contextData[ConfirmationModalWindowSharedContextInputKeys.aftermathTranslationKey];
    this.aftermathTranslationParams = this.aftermathTranslationParams
      ? this.aftermathTranslationParams
      : this.contextData[ConfirmationModalWindowSharedContextInputKeys.aftermathTranslationParams];
    this.aftermathTemplate = this.aftermathTemplate
      ? this.aftermathTemplate
      : (this.contextData[ConfirmationModalWindowSharedContextInputKeys.aftermathTemplate] as any);
    this.icon = this.icon ? this.icon : this.contextData[ConfirmationModalWindowSharedContextInputKeys.icon];
    this.confirmationHandlerFunction = this.confirmationHandlerFunction
      ? this.confirmationHandlerFunction
      : (this.contextData[ConfirmationModalWindowSharedContextInputKeys.confirmationHandlerFunction] as any);
    this.dismissHandlerFunction = this.dismissHandlerFunction
      ? this.dismissHandlerFunction
      : (this.contextData[ConfirmationModalWindowSharedContextInputKeys.dismissHandlerFunction] as any);
    this.successWindowSwitcherId = this.successWindowSwitcherId
      ? this.successWindowSwitcherId
      : this.contextData[ConfirmationModalWindowSharedContextInputKeys.successWindowSwitcherId];
    this.errorWindowSwitcherId = this.errorWindowSwitcherId
      ? this.errorWindowSwitcherId
      : this.contextData[ConfirmationModalWindowSharedContextInputKeys.errorWindowSwitcherId];
  }

  changeValue(value: boolean): void {
    this.localStorageValue = value;
  }
}
